/* eslint-disable max-classes-per-file */
import type { DailySummary, BusinessSettingsV2 } from '@am3nd-org/pointcraft-types';

import PlatformService from 'src/utils/Services/PlatformDBService';
import type {
  MonthlyTotals,
  YearlyReportResponse,
  MonthlyReportResponse,
  ThirtyDaySummaryResponse,
} from 'src/utils/fetch/reports';

import { useDeviceSyncStore } from 'src/store/DeviceSync';

import { isOfflineDemoActiveAtBoot } from './boot';
import {
  DEMO_USER_ID,
  DEMO_ROLE_ID,
  DEMO_BUSINESS_ID,
  DEMO_DEVICE_SYNC,
  deriveDailySummaries,
  createDemoSalesLedger,
  OFFLINE_DEMO_LEDGER_KEY,
  type OfflineDemoSaleFixture,
} from './fixtures';

export const OFFLINE_DEMO_DISABLED_MESSAGE =
  'This action is unavailable in Offline Demo. Phase one is local and read-only outside cart and safe settings.';

export class OfflineDemoActionDisabledError extends Error {
  readonly code = 'OFFLINE_DEMO_ACTION_DISABLED';

  constructor(message = OFFLINE_DEMO_DISABLED_MESSAGE) {
    super(message);
    this.name = 'OfflineDemoActionDisabledError';
  }
}

export class OfflineDemoUnhandledRequestError extends Error {
  readonly code = 'OFFLINE_DEMO_UNHANDLED_REQUEST';

  constructor(method: string, url: string) {
    super(`Offline Demo blocked an unregistered request: ${method} ${url}`);
    this.name = 'OfflineDemoUnhandledRequestError';
  }
}

type DemoRequest = {
  url: string;
  method: string;
  data?: unknown;
};

function parseRequestUrl(url: string): URL {
  return new URL(url, typeof window === 'undefined' ? 'http://localhost' : window.location.origin);
}

async function getLedger(): Promise<OfflineDemoSaleFixture[]> {
  const database = await PlatformService.getInstance('offline-demo');
  const record = await database.getSystemConfig<OfflineDemoSaleFixture[]>(OFFLINE_DEMO_LEDGER_KEY);
  return Array.isArray(record?.data) ? record.data : createDemoSalesLedger();
}

function summaryDate(summary: DailySummary): string {
  const date = summary.date instanceof Date ? summary.date : new Date(summary.date);
  return date.toISOString().slice(0, 10);
}

function emptyTotals(): MonthlyTotals {
  return {
    businessId: DEMO_BUSINESS_ID,
    timeZone: 'America/Chicago',
    gross_sales_cents: 0,
    cash_sales_cents: 0,
    net_sales_cents: 0,
    net_revenue_cents: 0,
    card_sales_cents: 0,
    tip_cents: 0,
    debt_amount_cents: 0,
    taxes_cents: 0,
    fees_cents: 0,
    refund_amount_cents: 0,
    number_of_sales: 0,
  };
}

function totalSummaries(summaries: readonly DailySummary[]): MonthlyTotals {
  return summaries.reduce<MonthlyTotals>((total, summary) => {
    total.gross_sales_cents += summary.gross_sales_cents;
    total.cash_sales_cents += summary.cash_sales_cents;
    total.net_sales_cents += summary.net_sales_cents;
    total.net_revenue_cents += summary.net_revenue_cents;
    total.card_sales_cents += summary.card_sales_cents;
    total.tip_cents += summary.tip_cents;
    total.debt_amount_cents += summary.debt_amount_cents;
    total.taxes_cents += summary.taxes_cents;
    total.fees_cents += summary.fees_cents;
    total.refund_amount_cents += summary.refund_amount_cents;
    total.number_of_sales += summary.number_of_sales;
    return total;
  }, emptyTotals());
}

function monthlyResponse(summaries: DailySummary[], month: string, year: number): MonthlyReportResponse {
  const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
  const selected = summaries.filter((summary) => {
    const date = new Date(summary.date);
    return date.getUTCFullYear() === year && date.getUTCMonth() === monthIndex;
  });
  const start = new Date(Date.UTC(year, monthIndex, 1));
  const end = new Date(Date.UTC(year, monthIndex + 1, 0));

  return {
    businessId: DEMO_BUSINESS_ID,
    month: month.toLowerCase(),
    year,
    timeZone: 'America/Chicago',
    dateRange: { start: start.toISOString(), end: end.toISOString() },
    dailySummaries: selected.map((summary) => ({
      ...summary,
      date: summaryDate(summary),
    })),
    monthlyTotal: totalSummaries(selected),
  };
}

function yearResponse(summaries: DailySummary[], year: number): YearlyReportResponse {
  const selected = summaries.filter((summary) => new Date(summary.date).getUTCFullYear() === year);
  const monthNames = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];

  return {
    businessId: DEMO_BUSINESS_ID,
    year,
    timeZone: 'America/Chicago',
    dateRange: {
      start: new Date(Date.UTC(year, 0, 1)).toISOString(),
      end: new Date(Date.UTC(year, 11, 31)).toISOString(),
    },
    dailySummaries: selected.map((summary) => ({ ...summary, date: summaryDate(summary) })),
    yearlyTotal: totalSummaries(selected),
    monthlyBreakdown: Object.fromEntries(
      monthNames.map((month) => {
        const response = monthlyResponse(summaries, month, year);
        return [month, { dailySummaries: response.dailySummaries, monthlyTotal: response.monthlyTotal }];
      })
    ),
  };
}

async function handleReports(url: URL): Promise<unknown> {
  const summaries = deriveDailySummaries(await getLedger());
  const date = url.searchParams.get('date');
  if (date) return summaries.find((summary) => summaryDate(summary) === date) ?? null;

  const month = url.searchParams.get('month');
  const year = Number(url.searchParams.get('year') ?? new Date().getUTCFullYear());
  if (month) return monthlyResponse(summaries, month, year);
  if (url.searchParams.has('year')) return yearResponse(summaries, year);

  if (url.searchParams.get('last30Days') === 'true') {
    return {
      data: summaries,
      pagination: {
        page: 1,
        pageSize: 30,
        totalPages: 1,
        totalItems: summaries.length,
        hasNextPage: false,
        hasPreviousPage: false,
      },
      pageTotals: {
        gross_sales_cents: totalSummaries(summaries).gross_sales_cents,
        net_sales_cents: totalSummaries(summaries).net_sales_cents,
        net_revenue_cents: totalSummaries(summaries).net_revenue_cents,
        number_of_sales: totalSummaries(summaries).number_of_sales,
      },
      dateRange: {
        start: summaryDate(summaries[summaries.length - 1]),
        end: summaryDate(summaries[0]),
      },
    } satisfies ThirtyDaySummaryResponse;
  }

  if (url.searchParams.has('period')) {
    return { ...totalSummaries(summaries), date: summaries[0]?.date ?? new Date(), period: url.searchParams.get('period') };
  }

  return summaries;
}

function updateDemoFees(data: unknown): { success: true } {
  const payload = data as {
    customerSplits?: { rate?: number; fixed?: number };
    taxRate?: number;
    acceptTips?: boolean;
  };
  useDeviceSyncStore.getState().updateBusinessData((business) => ({
    ...business,
    financialDefaults: {
      ...business.financialDefaults,
      ...(typeof payload.taxRate === 'number' ? { taxRate: payload.taxRate } : {}),
      ...(typeof payload.acceptTips === 'boolean' ? { acceptTips: payload.acceptTips } : {}),
      processingConfig: {
        ...business.financialDefaults.processingConfig,
        ...(payload.customerSplits
          ? {
              customerSplits: {
                rate: payload.customerSplits.rate ?? 0,
                fixed: payload.customerSplits.fixed ?? 0,
              },
            }
          : {}),
      },
    },
  }));
  return { success: true };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function deepMergeSettings(
  current: Record<string, unknown>,
  patch: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries({ ...current, ...patch }).map(([key, value]) => {
      const currentValue = asRecord(current[key]);
      const patchValue = asRecord(patch[key]);
      return [key, currentValue && patchValue ? deepMergeSettings(currentValue, patchValue) : value];
    })
  );
}

function updateDemoAccessV2Settings(data: unknown): { settings: BusinessSettingsV2 } {
  const payload = asRecord(data);
  if (!payload || Object.keys(payload).length === 0) {
    throw new OfflineDemoActionDisabledError('No safe Offline Demo setting was supplied.');
  }

  const topLevelKeys = Object.keys(payload);
  if (topLevelKeys.some((key) => key !== 'orders' && key !== 'printing')) {
    throw new OfflineDemoActionDisabledError();
  }

  const orders = payload.orders === undefined ? null : asRecord(payload.orders);
  if (
    (payload.orders !== undefined && !orders) ||
    (orders &&
      (Object.keys(orders).some((key) => key !== 'enabled') || orders.enabled !== false))
  ) {
    throw new OfflineDemoActionDisabledError('Order processing is disabled in Offline Demo.');
  }

  const printing = payload.printing === undefined ? null : asRecord(payload.printing);
  if (
    (payload.printing !== undefined && !printing) ||
    (printing &&
      (Object.keys(printing).some((key) => key !== 'footerText') ||
        typeof printing.footerText !== 'string'))
  ) {
    throw new OfflineDemoActionDisabledError();
  }

  let settings: BusinessSettingsV2 | null = null;
  useDeviceSyncStore.getState().updateBusinessData((business) => {
    const currentSettings = business.access_v2.businessAccess.settings;
    settings = deepMergeSettings(
      currentSettings as Record<string, unknown>,
      payload
    ) as BusinessSettingsV2;
    return {
      ...business,
      access_v2: {
        ...business.access_v2,
        businessAccess: {
          ...business.access_v2.businessAccess,
          settings,
        },
      },
    };
  });

  if (!settings) throw new OfflineDemoActionDisabledError('Business data is unavailable.');
  return { settings };
}

const DEMO_USER = {
  id: DEMO_USER_ID,
  name: 'Alex Demo Owner',
  email: 'owner@pointcraft-demo.local',
  phone: '(612) 555-0101',
  roleId: DEMO_ROLE_ID,
  role: null,
  userType: 'OWNER',
  isStoreAdmin: false,
  isDefaultUser: true,
  isAccountActive: true,
  hasPin: false,
  notificationPreferences: {
    receiveSecurityLoginAuthCodes: true,
    receiveNightlyReports: false,
    receiveMonthlyStatements: false,
    receiveMobileNotifications: false,
  },
  createdAt: '2026-01-01T12:00:00.000Z',
  updatedAt: '2026-08-01T12:00:00.000Z',
};

export async function handleOfflineDemoRequest(request: DemoRequest): Promise<unknown> {
  if (!isOfflineDemoActiveAtBoot()) {
    throw new Error('Offline demo request handler called outside Offline Demo');
  }

  const method = request.method.toUpperCase();
  const url = parseRequestUrl(request.url);
  const { pathname } = url;

  if (method === 'GET' && pathname === '/api/v4/sales/history') {
    const ledger = await getLedger();
    const date = url.searchParams.get('date');
    const filtered = date
      ? ledger.filter((sale) => sale.listItem.createdAt.startsWith(date))
      : ledger;
    return {
      data: filtered.map((sale) => sale.listItem),
      pageInfo: { limit: 100, hasMore: false, nextCursor: null },
    };
  }

  const detailMatch = pathname.match(/^\/api\/v4\/sale\/([^/]+)\/detail$/);
  if (method === 'GET' && detailMatch) {
    const sale = (await getLedger()).find((entry) => entry.listItem.id === detailMatch[1]);
    if (!sale) throw new Error('Demo sale not found');
    return sale.detail;
  }

  if (method === 'GET' && pathname === '/api/reports/daily-summary/v2') {
    return handleReports(url);
  }

  if (method === 'POST' && pathname === '/api/business/fees') {
    return updateDemoFees(request.data);
  }

  if (method === 'PATCH' && pathname === '/api/business/access/v2/settings') {
    return updateDemoAccessV2Settings(request.data);
  }

  if (method === 'PATCH' && pathname === '/api/business/profile') {
    throw new OfflineDemoActionDisabledError('Business profile changes are disabled in Offline Demo.');
  }

  if (method === 'GET' && pathname === '/api/business/integrations/status') {
    return {
      quickbooks: { connected: false },
      davos: { connected: false },
      pointpay: { connected: true },
      elevateAi: { connected: false },
      pointcraftInventory: { connected: true },
    };
  }

  if (method === 'GET' && pathname === '/api/business/users/v2') return [DEMO_USER];
  if (method === 'GET' && pathname === '/api/business/users/v2/roles') return [];
  if (method === 'GET' && pathname.startsWith('/api/business/devices/')) {
    return DEMO_DEVICE_SYNC.businessDevices ?? [];
  }

  if (method === 'GET' && pathname === '/status') {
    return { result: { status: 'offline', version: 'demo', environment: 'OFFLINE DEMO' } };
  }

  const isKnownDisabledMutation =
    method !== 'GET' &&
    (/^\/api\/v4\/(payment|sale)/.test(pathname) ||
      pathname.startsWith('/api/business/users') ||
      pathname.startsWith('/api/services/quickbooks') ||
      pathname.startsWith('/api/notify') ||
      pathname.startsWith('/api/reports/'));
  if (isKnownDisabledMutation) throw new OfflineDemoActionDisabledError();

  throw new OfflineDemoUnhandledRequestError(method, request.url);
}
